import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ItineraryDay, Meal, MealSlot, Venue } from '../../../types';
import { MEAL_SLOTS } from '../../../types';
import { SLOT_META } from './mealSlots';
import MealRow from './MealRow';
import MealPlacePicker from './MealPlacePicker';
import MealDayHeader from './MealDayHeader';
import MealDraftForm, { type MealDraft } from './MealDraftForm';
import type { IngredientActions } from './MealIngredients';
import { linkableStops, type MealStopActions } from './MealStopLink';
import type { PlaceBasicDetails } from '../../../api/places.api';
import { MapPin, Plus, X, Icon } from '../../ui/Icon';

interface Props {
  day: ItineraryDay;
  meals: Meal[];
  venues: Venue[];
  onPickPlace: (meal: Meal, place: PlaceBasicDetails) => Promise<void>;
  ingredients: IngredientActions;
  stops: MealStopActions;
  readOnly: boolean;
  open: boolean;
  isToday: boolean;
  onToggle: () => void;
  onAdd: (slot: MealSlot, draft: MealDraft) => Promise<Meal | null>;
  /** A meal that is a place: titled after it and linked to it. */
  onAddAtVenue: (slot: MealSlot, venueId: string) => Promise<Meal | null>;
  onAddAtPlace: (slot: MealSlot, place: PlaceBasicDetails) => Promise<Meal | null>;
  onRename: (mealId: string, title: string) => void;
  onUpdate: (mealId: string, patch: { notes?: string | null; venueId?: string | null }) => Promise<boolean>;
  onDelete: (mealId: string) => void;
}

/** The slots a normal day is expected to fill. `OTHER` is an escape hatch. */
const CORE_SLOTS = MEAL_SLOTS.filter(s => s !== 'OTHER');

/**
 * One day of the plan, with a row per slot.
 *
 * Every slot is always rendered, even empty: the point of the tab is seeing at
 * a glance that day 4 has no dinner yet, which a list of only what exists
 * cannot show. The counter in the header is the same question answered from
 * further away — you can scan the page for the gap without reading any row.
 * Folded, only that header stays, which is why it carries the slot icons.
 */
export default function MealDayCard({
  day, meals, venues, onPickPlace, ingredients, stops, readOnly, open, isToday, onToggle, onAdd, onAddAtVenue, onAddAtPlace, onRename, onUpdate, onDelete,
}: Props) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState<MealSlot | null>(null);
  const [picking, setPicking] = useState<MealSlot | null>(null);
  // One meal's editor open at a time. Lives here, not in the row, so a meal
  // created at a restaurant can be born open on its note and place.
  const [openMealId, setOpenMealId] = useState<string | null>(null);

  async function created(meal: Promise<Meal | null>) {
    const saved = await meal;
    if (saved) setOpenMealId(saved.id);
  }

  async function submit(slot: MealSlot, draft: MealDraft): Promise<boolean> {
    const saved = await onAdd(slot, draft);
    if (!saved) return false;
    setAdding(null);
    // ADR-0024: the stop needs the meal's id, so it is applied once the meal exists.
    if (draft.stop.kind === 'link') await stops.onLink(saved, draft.stop.activityId);
    if (draft.stop.kind === 'create') await stops.onCreate(saved, draft.stop.startTime || null);
    return true;
  }

  // OTHER only earns a row once something is in it — it is the escape hatch,
  // not a fifth meal everyone eats.
  const visibleSlots = MEAL_SLOTS.filter(
    s => s !== 'OTHER' || meals.some(m => m.slot === 'OTHER'),
  );
  const filledSlots = new Set(CORE_SLOTS.filter(s => meals.some(m => m.slot === s)));

  return (
    <section className={`card overflow-hidden ${isToday ? 'ring-1 ring-teal-200 dark:ring-teal-800' : ''}`}>
      <MealDayHeader
        day={day}
        coreSlots={CORE_SLOTS}
        filledSlots={filledSlots}
        open={open}
        isToday={isToday}
        onToggle={onToggle}
      />

      {open && (
        <div className="px-4 sm:px-5 pb-3 pt-1 border-t border-slate-100 dark:border-slate-800">
          {visibleSlots.map(slot => {
            const meta = SLOT_META[slot];
            const slotMeals = meals.filter(m => m.slot === slot);
            const label = t(`meals.slot.${slot}`);
            return (
              <div
                key={slot}
                className="flex items-start gap-3 py-2.5 border-t border-slate-100/80 dark:border-slate-800/80 first:border-t-0"
              >
                <div className={`shrink-0 mt-0.5 w-8 h-8 rounded-xl flex items-center justify-center ${meta.bg}`}>
                  <Icon as={meta.Icon} size={15} className={meta.color} />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-mono text-[10px] font-medium tracking-[0.12em] uppercase text-slate-400 dark:text-slate-600 mb-0.5">
                    {label}
                  </p>

                  {slotMeals.map(meal => (
                    <MealRow
                      key={meal.id}
                      meal={meal}
                      venues={venues}
                      readOnly={readOnly}
                      onRename={title => onRename(meal.id, title)}
                      onUpdate={patch => onUpdate(meal.id, patch)}
                      onDelete={() => onDelete(meal.id)}
                      onPickPlace={place => onPickPlace(meal, place)}
                      ingredients={ingredients}
                      stops={stops}
                      detailsOpen={openMealId === meal.id}
                      onToggleDetails={() => setOpenMealId(id => (id === meal.id ? null : meal.id))}
                    />
                  ))}

                  {adding === slot ? (
                    <MealDraftForm
                      products={ingredients.products}
                      linkable={linkableStops(stops.days, day.id)}
                      onSubmit={draft => submit(slot, draft)}
                      onCancel={() => setAdding(null)}
                    />
                  ) : picking === slot ? (
                    <div className="flex items-start gap-1.5 mt-1">
                      <div className="flex-1 min-w-0">
                        <MealPlacePicker
                          venues={venues}
                          venueId={null}
                          onSelectVenue={async venueId => {
                            if (!venueId) return;
                            setPicking(null);
                            await created(onAddAtVenue(slot, venueId));
                          }}
                          onPickPlace={async place => {
                            setPicking(null);
                            await created(onAddAtPlace(slot, place));
                          }}
                          emptyLabel={t('meals.pickSaved')}
                          autoFocusSearch
                        />
                      </div>
                      <button
                        onClick={() => setPicking(null)}
                        className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                        aria-label={t('common.cancel')}
                      >
                        <Icon as={X} size={13} />
                      </button>
                    </div>
                  ) : !readOnly ? (
                    <div className="flex flex-wrap items-center gap-x-2">
                      <button
                        onClick={() => { setPicking(null); setOpenMealId(null); setAdding(slot); }}
                        className="inline-flex items-center gap-1 rounded-lg px-1.5 py-1 -ml-1.5 text-xs text-slate-400 hover:text-teal-700 hover:bg-teal-50 dark:hover:text-teal-300 dark:hover:bg-teal-900/25 transition-colors cursor-pointer"
                      >
                        <Icon as={Plus} size={12} />
                        {t('meals.addTo', { slot: label.toLowerCase() })}
                      </button>
                      {/* A separate door on purpose: making the title field search
                          Places would bill for every "pasta con salmón" typed
                          (ADR-0001, ADR-0013). */}
                      <button
                        onClick={() => { setAdding(null); setPicking(slot); }}
                        className="inline-flex items-center gap-1 rounded-lg px-1.5 py-1 text-xs text-slate-400 hover:text-teal-700 hover:bg-teal-50 dark:hover:text-teal-300 dark:hover:bg-teal-900/25 transition-colors cursor-pointer"
                      >
                        <Icon as={MapPin} size={12} />
                        {t('meals.addAtPlace')}
                      </button>
                    </div>
                  ) : slotMeals.length === 0 ? (
                    <p className="text-xs text-slate-300 dark:text-slate-600 py-0.5">{t('meals.emptyDay')}</p>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
